/**
 * Card-level helpers for the Work board.
 *
 * Pure, so the rules can be asserted without a browser. The avatar colour is
 * derived from the name, so the same person is the same colour everywhere
 * without the server having to say so.
 *
 * @module web/screens/app/board/cards
 */

export const AVATAR_COLORS = [
  "#f97316", "#14b8a6", "#8b5cf6", "#ec4899", "#eab308", "#3b82f6", "#ef4444", "#22c55e",
] as const;

/** Stable per-name colour. Same person, same colour, everywhere. */
export function avatarColor(name: string | null | undefined): string {
  if (!name) return AVATAR_COLORS[0];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) % AVATAR_COLORS.length;
  return AVATAR_COLORS[h] ?? AVATAR_COLORS[0];
}

export function avatarInitial(name: string | null | undefined): string {
  return (name || "?").trim()[0]?.toUpperCase() ?? "?";
}

export interface DueLabel {
  label: string;
  overdue: boolean;
}

/**
 * The due date as the card shows it.
 *
 * One locale, en-IE, matching every other date and amount in the app.
 *
 * @param phase The item's `ClientPhase`, if known. A delivered card (which
 *   also covers the internal `handed_over` stage - `clientLifecycle` collapses
 *   both into this one client-facing phase) is never overdue: the work is
 *   already done, so a deadline that has since passed is not a problem to flag.
 */
export function formatDue(dateStr: string | null | undefined, phase?: string | null): DueLabel | null {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  return {
    label: d.toLocaleDateString("en-IE", { day: "numeric", month: "short" }),
    overdue: phase !== "delivered" && d.getTime() - Date.now() < 0,
  };
}

/** "just now" / "12m ago" / "3h ago" / "2d ago". */
export function timeAgo(iso: string | null | undefined): string {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const m = Math.floor((Date.now() - t) / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}
